/**
 * Database Navigator component.
 * Shows connections and tables in a tree view.
 * Inspired by DBeaver's Database Navigator.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Typography,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import TableChartIcon from '@mui/icons-material/TableChart';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Connection, TableInfo } from '../types';
import { connectionApi, schemaApi } from '../services/api';
import SvgIcon from './SvgIcon';

interface DatabaseNavigatorProps {
  onSelectConnection: (connection: Connection) => void;
  onNewConnection: () => void;
  onEditConnection: (connection: Connection) => void;
  onInsertTable: (table: TableInfo) => void;
  refreshTrigger?: number; // Trigger to refresh connections list
}

const DatabaseNavigator: React.FC<DatabaseNavigatorProps> = ({
  onSelectConnection,
  onNewConnection,
  onEditConnection,
  onInsertTable,
  refreshTrigger,
}) => {
  const [connections, setConnections] = useState<Connection[]>([]);
  const [expandedConnections, setExpandedConnections] = useState<Set<string>>(new Set());
  const [tables, setTables] = useState<Map<string, TableInfo[]>>(new Map());
  const [loadingTables, setLoadingTables] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadConnections();
  }, [refreshTrigger]); // Reload when refreshTrigger changes

  const loadConnections = async () => {
    try {
      const conns = await connectionApi.getAll();
      setConnections(conns);
    } catch (err) {
      console.error('Error loading connections:', err);
      setConnections([]);
    }
  };

  const handleToggleConnection = async (connId: string) => {
    const newExpanded = new Set(expandedConnections);
    
    if (newExpanded.has(connId)) {
      newExpanded.delete(connId);
    } else {
      newExpanded.add(connId);
      
      // Load tables if not already loaded
      if (!tables.has(connId)) {
        await loadTables(connId);
      }
    }
    
    setExpandedConnections(newExpanded);
  };

  const loadTables = async (connId: string) => {
    setLoadingTables(prev => new Set(prev).add(connId));
    
    try {
      const tableList = await schemaApi.getTables(connId);
      setTables(prev => new Map(prev).set(connId, tableList));
    } catch (err) {
      console.error('Failed to load tables:', err);
    } finally {
      setLoadingTables(prev => {
        const newSet = new Set(prev);
        newSet.delete(connId);
        return newSet;
      });
    }
  };

  const handleRefreshTables = async (connId: string) => {
    await loadTables(connId);
  };

  const handleDeleteConnection = async (connId: string) => {
    if (window.confirm('Delete this connection?')) {
      try {
        await connectionApi.delete(connId);
        loadConnections();
      } catch (err) {
        console.error('Failed to delete connection:', err);
      }
    }
  };

  const handleTableClick = (table: TableInfo) => {
    onInsertTable(table);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="subtitle2" fontWeight="bold">
          Database Navigator
        </Typography>
        <Tooltip title="New Connection">
          <IconButton size="small" onClick={onNewConnection}>
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Connections List */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {connections.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No connections. Click + to add one.
            </Typography>
          </Box>
        ) : (
          <List dense>
            {connections.map((conn) => {
              const isExpanded = expandedConnections.has(conn.id);
              const isLoading = loadingTables.has(conn.id);
              const connTables = tables.get(conn.id) || [];

              return (
                <React.Fragment key={conn.id}>
                  {/* Connection Item */}
                  <ListItem
                    disablePadding
                    secondaryAction={
                      <Box>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditConnection(conn);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteConnection(conn.id);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {isExpanded && (
                          <Tooltip title="Refresh">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRefreshTables(conn.id);
                              }}
                            >
                              <RefreshIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    }
                  >
                    <ListItemButton
                      onClick={() => {
                        onSelectConnection(conn);
                        handleToggleConnection(conn.id);
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                      </ListItemIcon>
                      <ListItemIcon sx={{ minWidth: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {conn.type === 'mysql' || conn.type === 'postgresql' ? (
                          <SvgIcon iconName={conn.type} size={24} />
                        ) : (
                          <StorageIcon fontSize="small" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={conn.name}
                        secondary={conn.type}
                      />
                    </ListItemButton>
                  </ListItem>

                  {/* Tables List */}
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {isLoading ? (
                        <ListItem sx={{ pl: 8 }}>
                          <CircularProgress size={20} />
                          <Typography variant="body2" sx={{ ml: 1 }}>
                            Loading tables...
                          </Typography>
                        </ListItem>
                      ) : connTables.length === 0 ? (
                        <ListItem sx={{ pl: 8 }}>
                          <Typography variant="body2" color="text.secondary">
                            No tables found
                          </Typography>
                        </ListItem>
                      ) : (
                        connTables.map((table) => (
                          <ListItemButton
                            key={table.name}
                            sx={{ pl: 8 }}
                            onClick={() => handleTableClick(table)}
                          >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <TableChartIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={table.name}
                              secondary={table.type}
                            />
                          </ListItemButton>
                        ))
                      )}
                    </List>
                  </Collapse>
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Box>
    </Box>
  );
};

export default DatabaseNavigator;

